/**
 * In-app product tour - step catalog and chrome events.
 * Storyboard SoT: docs/product/bets/2026-08-28-in-app-product-tour.md
 */

import { testIds } from '@/lib/test-ids'

export const PRODUCT_TOUR_START_EVENT = 'flownotes:product-tour-start'
export const PRODUCT_TOUR_WORKSPACE_SCOPE_EVENT = 'flownotes:product-tour-workspace-scope'
/** Set on <html> while the tour overlay is active - raises Radix menus above the dim. */
export const PRODUCT_TOUR_ACTIVE_ATTR = 'data-product-tour-active'

export type ProductTourWorkspaceScopeDetail = { open: boolean }

export type ProductTourStepAction = 'open-workspace-scope' | 'close-workspace-scope'

export type ProductTourRect = {
  top: number
  left: number
  width: number
  height: number
}

export type ProductTourStep = {
  id: string
  title: string
  body: string
  /** `data-testid` of the spotlight target; null = full-dim welcome/done */
  testId: string | null
  /**
   * Extra surfaces to include in the spotlight when open (e.g. a flyout).
   * Lets users see the menu without covering it with the tour card.
   */
  expandTestIds?: string[]
  action?: ProductTourStepAction
  /** When true, skip this step if the target is missing from the DOM */
  skipIfMissing?: boolean
  /** Welcome / deck: show interactive shell anatomy wireframe in the tour card */
  showAnatomy?: boolean
  /**
   * Prefer parking the tour card beside the spotlight.
   * `beside-end` = prefer right of target (left-rail / flyouts).
   * `beside-start` = prefer left of target (header-right / agent).
   * `below` = prefer under a wide short target (tab bars).
   */
  tooltipBias?: 'auto' | 'beside-end' | 'beside-start' | 'below'
  /**
   * Clicking the spotlight target advances to the next step
   * (e.g. workspace picker -> open list). Next also advances.
   */
  advanceOnTargetActivate?: boolean
}

export const PRODUCT_TOUR_STEPS: ProductTourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Maater',
    body: 'Your notes, and the agent that works from them. Hover the map to light up the real chrome - then we walk each region live.',
    testId: null,
    showAnatomy: true,
  },
  {
    id: 'search',
    title: 'Search anything',
    body: 'Find a doc, chat, or todo.',
    testId: testIds.header.search,
  },
  {
    id: 'profile',
    title: 'Your account',
    body: 'This is you. It shows your email until you set a display name under Account, after the tour.',
    testId: testIds.header.userMenu,
    tooltipBias: 'beside-start',
  },
  {
    id: 'help',
    title: 'Help and this tour',
    body: 'Click ? for Take a tour, keyboard shortcuts, Getting started, Connect MCP, and What\'s new. Try the menu if you like; Next continues.',
    testId: testIds.header.helpMenu,
    tooltipBias: 'beside-start',
  },
  {
    id: 'panel-toggles',
    title: 'Show or hide panels',
    body: 'PanelLeft on the rail shows or hides the sidebar (Cmd/Ctrl+B). The Bot on the agent header shows or hides the agent (Cmd/Ctrl+J).',
    testId: testIds.shellToggle.left,
    tooltipBias: 'beside-end',
  },
  {
    id: 'workspace-picker',
    title: 'Pick your workspaces',
    body: 'This is the workspace picker. If Browse looks empty, a workspace is probably off. Click it (or Next) to open the list.',
    testId: testIds.leftSidebar.workspaceScopeButton,
    action: 'close-workspace-scope',
    advanceOnTargetActivate: true,
    tooltipBias: 'beside-end',
  },
  {
    id: 'workspace-list',
    title: 'Active workspaces',
    body: 'Turn workspaces on or off here. Click the list if you want - Next continues the tour.',
    testId: testIds.leftSidebar.workspaceScopeButton,
    expandTestIds: [testIds.leftSidebar.workspaceScopePopover],
    action: 'open-workspace-scope',
    tooltipBias: 'beside-end',
  },
  {
    id: 'icon-bar',
    title: 'Icon rail',
    body: 'Browse, Inbox, Flows, and Chats are peer modes on the rail. Settings lives at the bottom.',
    testId: testIds.leftSidebar.iconBarCore,
    action: 'close-workspace-scope',
    tooltipBias: 'beside-end',
  },
  {
    id: 'icon-bar-extensions',
    title: 'Extensions',
    body: 'Other tools sit below the divider. Hover the rail to reveal +, or use + to open Workspace Settings -> Extensions.',
    testId: testIds.leftSidebar.iconBarExtensions,
    expandTestIds: [testIds.leftSidebar.railAddExtension],
    tooltipBias: 'beside-end',
  },
  {
    id: 'sidebar',
    title: 'Mode panel',
    body: 'The list beside the icons follows the icon you pick.',
    testId: testIds.leftSidebar.panel,
    skipIfMissing: true,
    tooltipBias: 'beside-end',
  },
  {
    id: 'main-panel',
    title: 'Main panel',
    body: 'Home, documents, inbox threads, and chats all open here.',
    testId: testIds.resizableLayout.mainArea,
    skipIfMissing: true,
  },
  {
    id: 'tab-bar',
    title: 'Tabs',
    body: 'What you open stays up here.',
    testId: testIds.documentViewer.tabList,
    skipIfMissing: true,
    tooltipBias: 'below',
  },
  {
    id: 'agent-panel',
    title: 'Agent panel',
    body: 'It already sees the document you have open. Type @ to bring in another doc or folder. Under the box: Agent does the work, Plan drafts first, Ask only answers. The name beside that is the model.',
    testId: testIds.resizableLayout.chatPane,
    skipIfMissing: true,
    tooltipBias: 'beside-start',
  },
  {
    id: 'done',
    title: "You're set",
    body: 'Replay anytime from Help (?). Next: open a doc and ask the agent about it.',
    testId: null,
  },
]

export function startProductTour(): void {
  window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_START_EVENT))
}

export function setProductTourWorkspaceScopeOpen(open: boolean): void {
  window.dispatchEvent(
    new CustomEvent(PRODUCT_TOUR_WORKSPACE_SCOPE_EVENT, {
      detail: { open } satisfies ProductTourWorkspaceScopeDetail,
    }),
  )
}

const PEEK_TOGGLE_SELECTOR =
  '[data-testid$="preview-toggle"], [data-testid="document-viewer-info-button"]'
const PEEK_PANE_SELECTOR =
  '[data-testid$="preview-pane"], [data-testid$="preview-empty"], [data-testid="document-info-dock"]'

let peekToggle: HTMLElement | null = null
let peekOpenedByTour = false

function peekElementVisible(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false
  if (el.closest('[data-testid="product-tour-root"]')) return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

function peekControlIsOpen(el: HTMLElement): boolean {
  if (el.getAttribute('aria-pressed') === 'true') return true
  const title = el.getAttribute('title') ?? ''
  if (/^Hide /i.test(title)) return true
  if (el.dataset.testid === testIds.documentViewer.infoButton) {
    const dock = document.querySelector(`[data-testid="${testIds.documentInfoDock.root}"]`)
    return Boolean(dock && peekElementVisible(dock))
  }
  return false
}

function findPeekToggle(): HTMLElement | null {
  return [...document.querySelectorAll(PEEK_TOGGLE_SELECTOR)].find(peekElementVisible) ?? null
}

/** Welcome-map dashed slice: open the view's secondary panel, then close it on leave. */
export function setProductTourPeekOpen(open: boolean): void {
  if (typeof document === 'undefined') return
  if (open) {
    if (peekOpenedByTour) return
    const toggle = findPeekToggle()
    if (!toggle) return
    peekToggle = toggle
    if (peekControlIsOpen(toggle)) return
    peekOpenedByTour = true
    toggle.click()
    return
  }
  const toggle = peekToggle
  const shouldClose = peekOpenedByTour
  peekOpenedByTour = false
  peekToggle = null
  if (!shouldClose || !toggle?.isConnected) return
  const close = () => {
    if (toggle.isConnected && peekControlIsOpen(toggle)) toggle.click()
  }
  if (peekControlIsOpen(toggle)) close()
  else requestAnimationFrame(() => requestAnimationFrame(close))
}

export function getProductTourPeekRect(): ProductTourRect | null {
  if (typeof document === 'undefined') return null
  const pane = [...document.querySelectorAll(PEEK_PANE_SELECTOR)].find(peekElementVisible)
  const paneRect = pane ? rectFromEl(pane, 0) : null
  if (!paneRect) return null
  const main = document.querySelector(`[data-testid="${testIds.resizableLayout.mainArea}"]`)
  const mainRect = main ? rectFromEl(main, 0) : null
  if (!mainRect) return paneRect
  // Sit on the main area's right and bottom edges so the tab chrome and
  // status row do not leave a main-panel strip beside the peek.
  const right = mainRect.left + mainRect.width
  const bottom = mainRect.top + mainRect.height
  return {
    top: paneRect.top,
    left: paneRect.left,
    width: Math.max(1, right - paneRect.left),
    height: Math.max(1, bottom - paneRect.top),
  }
}

function rectFromEl(el: Element, padding: number): ProductTourRect | null {
  if (!(el instanceof HTMLElement)) return null
  const rect = el.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null
  // Integer pixels - subpixel + soft ring read as "taller/wider" than the chrome
  const top = Math.round(Math.max(0, rect.top - padding))
  const left = Math.round(Math.max(0, rect.left - padding))
  const right = Math.round(rect.right + padding)
  const bottom = Math.round(rect.bottom + padding)
  return {
    top,
    left,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  }
}

function unionRects(a: ProductTourRect, b: ProductTourRect): ProductTourRect {
  const top = Math.min(a.top, b.top)
  const left = Math.min(a.left, b.left)
  const right = Math.max(a.left + a.width, b.left + b.width)
  const bottom = Math.max(a.top + a.height, b.top + b.height)
  return { top, left, width: right - left, height: bottom - top }
}

export function getProductTourTargetRect(
  testId: string | null,
  padding = 8,
  expandTestIds: string[] = [],
  options?: {
    /**
     * When a flyout is open, hug that surface (and the trigger if nearby).
     * Avoids oversized holes from mid-animation / wrapper rects.
     */
    preferExpand?: boolean
  },
): ProductTourRect | null {
  if (!testId || typeof document === 'undefined') return null
  const primary = document.querySelector(`[data-testid="${testId}"]`)
  const primaryRect = primary ? rectFromEl(primary, padding) : null

  const expandRects: ProductTourRect[] = []
  for (const id of expandTestIds) {
    const el = document.querySelector(`[data-testid="${id}"]`)
    if (!el) continue
    // Skip closed / animating-out menus (Radix may leave them mounted)
    if (el instanceof HTMLElement) {
      const state = el.getAttribute('data-state')
      if (state === 'closed') continue
    }
    const extra = rectFromEl(el, padding)
    if (!extra) continue
    expandRects.push(extra)
  }

  if (options?.preferExpand && expandRects.length > 0) {
    let combined = expandRects[0]
    for (let i = 1; i < expandRects.length; i++) {
      combined = unionRects(combined, expandRects[i])
    }
    // Include trigger only when it sits against the flyout (same cluster)
    if (primaryRect && rectsNear(primaryRect, combined, 48)) {
      combined = unionRects(combined, primaryRect)
    }
    return combined
  }

  let combined = primaryRect
  for (const extra of expandRects) {
    combined = combined ? unionRects(combined, extra) : extra
  }
  return combined
}

function rectsNear(a: ProductTourRect, b: ProductTourRect, gapPx: number): boolean {
  const ax2 = a.left + a.width
  const ay2 = a.top + a.height
  const bx2 = b.left + b.width
  const by2 = b.top + b.height
  const sepX = Math.max(0, Math.max(a.left - bx2, b.left - ax2))
  const sepY = Math.max(0, Math.max(a.top - by2, b.top - ay2))
  return sepX <= gapPx && sepY <= gapPx
}

/** Center-to-center distance - used to decide crossfade vs local nudge. */
export function productTourRectTravel(a: ProductTourRect, b: ProductTourRect): number {
  const ax = a.left + a.width / 2
  const ay = a.top + a.height / 2
  const bx = b.left + b.width / 2
  const by = b.top + b.height / 2
  return Math.hypot(ax - bx, ay - by)
}

export function isProductTourTargetPresent(testId: string | null): boolean {
  if (!testId || typeof document === 'undefined') return false
  const el = document.querySelector(`[data-testid="${testId}"]`)
  return el instanceof HTMLElement
}

/** Park the tour card beside the spotlight (not across the screen). */
export function placeProductTourTooltip(
  hole: ProductTourRect | null,
  bias: ProductTourStep['tooltipBias'] = 'auto',
  tooltipWidth = 320,
  estimatedHeight = 200,
): { top: number; left: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const gap = 12
  const margin = 16

  if (!hole) {
    return {
      top: Math.max(margin, (vh - estimatedHeight) / 2),
      left: Math.max(margin, (vw - tooltipWidth) / 2),
    }
  }

  const clamp = (left: number, top: number) => ({
    left: Math.min(Math.max(margin, left), vw - tooltipWidth - margin),
    top: Math.min(Math.max(margin, top), vh - estimatedHeight - margin),
  })

  const spaceRight = vw - (hole.left + hole.width) - margin
  const spaceLeft = hole.left - margin
  const spaceBelow = vh - (hole.top + hole.height) - margin
  const spaceAbove = hole.top - margin

  type Spot = { left: number; top: number; fits: boolean; order: number }
  const right: Spot = {
    left: hole.left + hole.width + gap,
    top: hole.top,
    fits: spaceRight >= tooltipWidth && hole.top + estimatedHeight <= vh - margin,
    order: 0,
  }
  const left: Spot = {
    left: hole.left - tooltipWidth - gap,
    top: hole.top,
    fits: spaceLeft >= tooltipWidth && hole.top + estimatedHeight <= vh - margin,
    order: 0,
  }
  const below: Spot = {
    left: hole.left + hole.width / 2 - tooltipWidth / 2,
    top: hole.top + hole.height + gap,
    fits: spaceBelow >= estimatedHeight,
    order: 0,
  }
  const above: Spot = {
    left: hole.left + hole.width / 2 - tooltipWidth / 2,
    top: hole.top - estimatedHeight - gap,
    fits: spaceAbove >= estimatedHeight,
    order: 0,
  }

  // Large panel (main area): park the card inside the top of the cutout
  const isLargePanel =
    hole.width > vw * 0.35 && hole.height > Math.max(estimatedHeight * 1.5, vh * 0.35)
  if (isLargePanel && bias !== 'beside-end' && bias !== 'beside-start' && bias !== 'below') {
    return clamp(hole.left + Math.max(gap, 24), hole.top + Math.max(gap, 24))
  }

  // Prefer beside the target on the roomier / biased side, then below/above
  const preferEnd =
    bias === 'beside-end' ||
    (bias === 'auto' && spaceRight >= spaceLeft)
  const ranked =
    bias === 'below'
      ? [below, above, right, left]
      : preferEnd
        ? [right, below, left, above]
        : bias === 'beside-start'
          ? [left, below, right, above]
          : [spaceRight >= spaceLeft ? right : left, below, spaceRight >= spaceLeft ? left : right, above]

  ranked.forEach((s, i) => {
    s.order = i
  })

  const chosen = ranked.find((s) => s.fits) ?? ranked[0]
  return clamp(chosen.left, chosen.top)
}
